/**
 * Surfaces the `FEATURE_*` error codes the Admin API returns when a dial is
 * rejected (e.g. a percent out of range, or a stage move blocked by policy).
 * GraphQL errors can carry the code in `extensions.code`; network/relay errors
 * only carry a message, so we also scan free text. Everything here is a pure
 * function so it can be unit-tested without a Relay harness.
 */

const CODE_PATTERN = /FEATURE_[A-Z0-9_]+/;

interface CodeCarrier {
    message?: unknown;
    extensions?: { code?: unknown } | null;
}

function codeFromExtensions(error: CodeCarrier): string | null {
    const code = error.extensions?.code;
    return typeof code === 'string' && CODE_PATTERN.test(code) ? code : null;
}

function codeFromText(text: unknown): string | null {
    return typeof text === 'string' ? (text.match(CODE_PATTERN)?.[0] ?? null) : null;
}

/**
 * Extract the first `FEATURE_*` code from a GraphQL error array, a thrown
 * `Error`, or a single error object. Returns `null` when none is present.
 */
export function featureErrorCode(source: unknown): string | null {
    if (source == null) return null;
    if (Array.isArray(source)) {
        for (const error of source) {
            const code = codeFromExtensions(error ?? {}) ?? codeFromText(error?.message);
            if (code) return code;
        }
        return null;
    }
    if (source instanceof Error) return codeFromText(source.message);
    const carrier = source as CodeCarrier;
    return codeFromExtensions(carrier) ?? codeFromText(carrier.message);
}

/** Turn `FEATURE_KILL_SWITCH_ACTIVE` into a readable `Kill switch active`. */
export function humanizeFeatureCode(code: string): string {
    const words = code.replace(/^FEATURE_/, '').toLowerCase().replace(/_/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Build the `{ title, description }` for an error toast. Prefers a surfaced
 * `FEATURE_*` code (shown verbatim as the description, with a humanised title);
 * falls back to a generic message otherwise.
 */
export function featureErrorToast(source: unknown): { title: string; description: string } {
    const code = featureErrorCode(source);
    if (code) {
        return { title: humanizeFeatureCode(code), description: code };
    }
    return {
        title: "Couldn't update the flag",
        description: 'The change was rejected. Please try again.',
    };
}
